import type {
  LevelCompleteResponse,
  LevelDoubleRewardResponse,
  LevelFailedResponse,
  PurchaseItem,
  PurchaseResponse,
  ReviveOfferResponse,
  RewardBoosterResponse,
  RewardBoosterType,
  RewardHeartResponse,
} from "../api/dto/player.dto.js";
import { ApiError } from "../api/http_client.js";
import { CrashBreadcrumbs } from "../crash/breadcrumbs.js";
import {
  APP_RUN_ID,
  completeLevel as completeLevelRequest,
  doubleLevelReward as doubleLevelRewardRequest,
  getReviveOffer as getReviveOfferRequest,
  purchaseItem as purchaseItemRequest,
  reportLevelFailed as reportLevelFailedRequest,
  reportLevelStart as reportLevelStartRequest,
  rewardBooster as rewardBoosterRequest,
  rewardHeart as rewardHeartRequest,
  type LevelStartResponse,
} from "../api/requests/economy.requests.js";
import { LevelPackService } from "../level_pack/level_pack_service.js";
import { WinStreakTowerService } from "../win_streak_tower/win_streak_tower_service.js";
import { StatisticsStorage } from "../storage/statistics_storage.js";
import { isAmbiguousNetworkFailure, isNetworkishError } from "../utils/network_errors.js";
import { OfflineJournal } from "./offline_journal.js";
import { shouldGoLocal } from "./offline_policy.js";
import { OfflineEconomy, difficultyName } from "./offline_economy.js";
import { LocalMetrics } from "./local_metrics.js";
import { KeyHuntSignals } from "../key_hunt/key_hunt_signals.js";

const REWARDED_BOOSTER_INVENTORY: Record<RewardBoosterType, "fireworks" | "multi_balls" | "big_balls" | "infinite_balls" | "rockets" | "mines"> = {
  firework: "fireworks",
  five_balls: "multi_balls",
  big_ball: "big_balls",
  infinite_shots: "infinite_balls",
  rocket: "rockets",
  mines: "mines",
};

let active_level: number | null = null;
let revive_purchases_on_attempt = 0;


function isoOrNull( ms: number ): string | null
{
  return ms > 0 ? new Date( ms ).toISOString() : null;
}

function localLevelStart( level: number ): LevelStartResponse
{
  const journal = OfflineJournal.list();
  const last = journal[journal.length - 1];
  const duplicate = last?.type === "level_start" && last.payload.level === level && last.payload.app_run_id === APP_RUN_ID;
  if ( !duplicate )
  {
    OfflineJournal.append( "level_start", { level, app_run_id: APP_RUN_ID } );
    LocalMetrics.report( [ { metric: "play_attempts", amount: 1 } ] );
  }
  return {
    ok: true,
    attempt_number: 0,
    attempt_mass_multiplier: 1,
    attempt_mass: { multiplier: 1, source: "offline" },
  };
}

export async function reportLevelStart( level: number ): Promise<LevelStartResponse>
{
  CrashBreadcrumbs.add( 'level', `start ${level}${shouldGoLocal() ? ' (offline)' : ''}` );
  active_level = level;
  revive_purchases_on_attempt = 0;

  if ( shouldGoLocal() )
    return localLevelStart( level );

  try
  {
    return await reportLevelStartRequest( level );
  }
  catch ( e )
  {
    if ( !isNetworkishError( e ) ) throw e;
    return localLevelStart( level );
  }
}

function localLevelComplete( level: number, stars: number ): LevelCompleteResponse
{
  const difficulty = difficultyName( LevelPackService.getDifficulty( level ) );
  const granted = OfflineEconomy.completeLevel( level, stars, difficulty );
  LocalMetrics.report( [ { metric: "level_streak", amount: LocalMetrics.bumpSessionStreak() } ] );
  const win_streak_tower = WinStreakTowerService.applyLocalWin();
  OfflineJournal.append( "level_complete", { level, stars } );
  active_level = null;
  return { player: OfflineEconomy.localPlayerDto(), granted, win_streak_tower };
}

export async function completeLevel( level: number, stars: number ): Promise<LevelCompleteResponse>
{
  CrashBreadcrumbs.add( 'level', `complete ${level} stars=${stars}${shouldGoLocal() ? ' (offline)' : ''}` );
  if ( shouldGoLocal() )
    return localLevelComplete( level, stars );

  try
  {
    const response = await completeLevelRequest( level, stars );
    active_level = null;
    LocalMetrics.bumpSessionStreak();
    KeyHuntSignals.serverProgressMayHaveChanged();
    return response;
  }
  catch ( e )
  {
    if ( !isNetworkishError( e ) ) throw e;
    return localLevelComplete( level, stars );
  }
}

function localLevelFailed(): LevelFailedResponse
{
  const settled = OfflineEconomy.spendHeart();
  const win_streak_tower = WinStreakTowerService.applyLocalLoss();
  OfflineJournal.append( "level_failed", { level: active_level } );
  active_level = null;
  return {
    hearts: settled.hearts,
    max_hearts: settled.max_hearts,
    next_heart_at: isoOrNull( settled.next_heart_at ),
    win_streak_tower,
  };
}

export async function reportLevelFailed(): Promise<LevelFailedResponse>
{
  CrashBreadcrumbs.add( 'level', `failed ${active_level ?? '?'}${shouldGoLocal() ? ' (offline)' : ''}` );
  if ( shouldGoLocal() )
    return localLevelFailed();

  try
  {
    const response = await reportLevelFailedRequest();
    active_level = null;
    return response;
  }
  catch ( e )
  {
    if ( !isNetworkishError( e ) ) throw e;
    return localLevelFailed();
  }
}

function localReviveOffer(): ReviveOfferResponse
{
  const level = active_level ?? StatisticsStorage.load().level;
  return OfflineEconomy.reviveOffer( level, revive_purchases_on_attempt );
}

export async function getReviveOffer(): Promise<ReviveOfferResponse>
{
  if ( shouldGoLocal() )
    return localReviveOffer();

  try
  {
    return await getReviveOfferRequest();
  }
  catch ( e )
  {
    if ( !isNetworkishError( e ) ) throw e;
    return localReviveOffer();
  }
}

function localPurchase(
  item: PurchaseItem,
  expected?: { purchase_index?: number; price: number; qty: number }
): PurchaseResponse
{
  const economy = OfflineEconomy.getEconomyConfig();
  const stats = StatisticsStorage.load();
  let price: number;
  let qty: number;
  let purchase_index: number | undefined;

  if ( item === "continue_balls" )
  {
    const offer = localReviveOffer();
    if ( !offer.available || !offer.offer )
      throw new ApiError( 409, "revive_unavailable" );
    if ( expected?.purchase_index !== undefined && expected.purchase_index !== offer.purchase_index )
      throw new ApiError( 409, "revive_offer_changed" );
    price = offer.offer.price;
    qty = offer.offer.shots;
    purchase_index = offer.purchase_index;
  }
  else if ( item === "fireworks" )
  {
    price = stats.fireworks_price || economy.fireworks_price;
    qty = stats.fireworks_pack_qty || economy.fireworks_pack_qty;
  }
  else
  {
    price = stats.heart_refill_price || economy.heart_refill_price;
    qty = 1;
  }

  if ( expected && ( expected.price !== price || expected.qty !== qty ) )
    throw new ApiError( 409, `${item}_offer_changed` );

  const result = OfflineEconomy.purchase( item, price, qty );
  if ( !result.ok )
    throw new ApiError( 409, result.reason );

  OfflineJournal.append( "purchase", { item, price, qty, ...( purchase_index !== undefined ? { purchase_index } : {} ) } );
  if ( item === "continue_balls" )
    revive_purchases_on_attempt++;

  return {
    player: OfflineEconomy.localPlayerDto(),
    purchased: {
      item,
      price,
      qty,
      ...( purchase_index !== undefined
        ? { purchase_index, remaining_purchases: Math.max( 0, localReviveOffer().remaining_purchases ) }
        : {} ),
    },
  };
}

export async function purchaseItem(
  item: PurchaseItem,
  expected?: { purchase_index?: number; price: number; qty: number }
): Promise<PurchaseResponse>
{
  CrashBreadcrumbs.add( 'app', `purchase ${String( item )}${expected ? ` price=${expected.price} qty=${expected.qty}` : ''}${shouldGoLocal() ? ' (offline)' : ''}` );
  if ( shouldGoLocal() )
    return localPurchase( item, expected );

  try
  {
    const response = await purchaseItemRequest( item, expected );
    if ( item === "continue_balls" )
      revive_purchases_on_attempt++;
    return response;
  }
  catch ( e )
  {
    if ( !isNetworkishError( e ) || isAmbiguousNetworkFailure( e ) ) throw e;
    return localPurchase( item, expected );
  }
}

function localDoubleReward( level: number, stars: number ): LevelDoubleRewardResponse
{
  const difficulty = difficultyName( LevelPackService.getDifficulty( level ) );
  const coins = OfflineEconomy.doubleLevelReward( level, stars, difficulty );
  OfflineJournal.append( "level_double_reward", { level, stars } );
  return { player: OfflineEconomy.localPlayerDto(), granted: { coins, points: 0 } };
}

export async function doubleLevelReward( level: number, stars: number ): Promise<LevelDoubleRewardResponse>
{
  CrashBreadcrumbs.add( 'app', `double_reward ${level}` );
  if ( shouldGoLocal() )
    return localDoubleReward( level, stars );

  try
  {
    return await doubleLevelRewardRequest( level, stars );
  }
  catch ( e )
  {
    if ( !isNetworkishError( e ) ) throw e;
    return localDoubleReward( level, stars );
  }
}

function localRewardHeart(): RewardHeartResponse
{
  const stats = StatisticsStorage.load();
  if ( stats.hearts >= stats.max_hearts )
    throw new ApiError( 409, "hearts_full" );
  OfflineEconomy.addHearts( 1 );
  OfflineJournal.append( "reward_heart", {} );
  LocalMetrics.report( [ { metric: "watch_rewarded_ads", amount: 1, param: "rewarded" } ] );
  return { player: OfflineEconomy.localPlayerDto() };
}

export async function rewardHeart(): Promise<RewardHeartResponse>
{
  CrashBreadcrumbs.add( 'app', 'rewarded heart' );
  if ( shouldGoLocal() )
    return localRewardHeart();

  try
  {
    return await rewardHeartRequest();
  }
  catch ( e )
  {
    if ( !isNetworkishError( e ) || isAmbiguousNetworkFailure( e ) ) throw e;
    return localRewardHeart();
  }
}

function localRewardBooster( booster_type: RewardBoosterType, placement: string, ad_id: string ): RewardBoosterResponse
{
  OfflineEconomy.addBoosters( { [ REWARDED_BOOSTER_INVENTORY[booster_type] ]: 1 } );
  OfflineJournal.append( "reward_booster", { booster_type, placement, ad_id } );
  return {
    player: OfflineEconomy.localPlayerDto(),
    granted: { booster_type, quantity: 1, placement, ad_id },
    idempotent: false,
  };
}

export async function rewardBooster(
  booster_type: RewardBoosterType,
  placement: string,
  ad_id: string
): Promise<RewardBoosterResponse>
{
  CrashBreadcrumbs.add( 'app', `rewarded booster ${String( booster_type )} @${placement}` );
  if ( shouldGoLocal() )
    return localRewardBooster( booster_type, placement, ad_id );

  try
  {
    return await rewardBoosterRequest( booster_type, placement, ad_id );
  }
  catch ( e )
  {
    if ( !isNetworkishError( e ) ) throw e;
    return localRewardBooster( booster_type, placement, ad_id );
  }
}
